import type { CampaignCapabilities } from '@/domain/campaign.types'
import { canDeleteOwnedBy, capabilitiesFor } from '@/lib/capabilities'

// Campaign's slice of the shared role ladder ({@link capabilitiesFor}), named in
// the terms the Campaign surfaces speak. Not a port: it has no in-memory-versus-
// API variants to choose between, so it is never selected or injected through the
// feature composition boundary.
//
// Carries no read capability — Campaign read access is owned exclusively by route
// authorization, and every role may read.

const NO_CAPABILITIES: CampaignCapabilities = {
	canCreate: false,
	canEdit: false,
	canTransition: false,
	canAssociateFiles: false,
	canUploadFiles: false,
	canAssociateMessages: false,
	canTranslateMessages: false,
	canEditMessages: false,
	canDelete: false,
}

/**
 * Resolve a Campaign session's action capabilities.
 *
 * Everything except delete is the flat "may work" cut: USER and above create,
 * edit, transition, associate and translate; a read-only role does none of it.
 *
 * Delete is the exception, because it is the one action scoped by ownership.
 * `ownerId` is the Campaign's `createdBy` where a single Campaign is in hand;
 * omitting it (a list, where there is no one owner) resolves delete for
 * administrators only.
 */
export function campaignCapabilitiesFor(
	role: string | undefined | null,
	owner?: { userId: string | undefined | null; ownerId: string | null | undefined },
): CampaignCapabilities {
	const { canWork } = capabilitiesFor(role)
	if (!canWork) return NO_CAPABILITIES

	return {
		canCreate: true,
		canEdit: true,
		canTransition: true,
		canAssociateFiles: true,
		canUploadFiles: true,
		canAssociateMessages: true,
		canTranslateMessages: true,
		canEditMessages: true,
		canDelete: canDeleteOwnedBy(role, owner?.userId, owner?.ownerId),
	}
}
